import { useApolloClient } from '@apollo/client/react';
import { eachDayOfInterval, format, isValid, parseISO } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';

import { LIST_MEAL_TIMESTAMPS_QUERY } from '../libs/graphQL';

interface UseMealTimestampRangeKpisParams {
  kitchen: string;
  startDate: string;
  endDate: string;
  enabled?: boolean;
  includeTimestamps?: boolean;
}

export interface MealTimestampListItem {
  mealId: string;
  timestamp: string;
  name?: string | null;
  station?: string;
  date: string;
}

interface RawMealTimestampRecord {
  mealId: string;
  station?: string | null;
  timestamp: string;
  name?: string | null;
}

type ListMealTimestampsResponse = {
  listMealTimestamps?: {
    data?: RawMealTimestampRecord[];
    next?: string | null;
  };
};

const MAX_CONCURRENT_PARTITION_QUERIES = 5;

const toDateList = (startDate: string, endDate: string) => {
  const start = parseISO(startDate);
  const end = parseISO(endDate);
  if (!isValid(start) || !isValid(end) || start > end) {
    return [];
  }

  return eachDayOfInterval({ start, end }).map((day) => format(day, 'yyyy-MM-dd'));
};

async function runWithConcurrency<T>(items: T[], concurrency: number, worker: (item: T) => Promise<void>) {
  let index = 0;
  const size = Math.min(concurrency, items.length);

  const runners = Array.from({ length: size }, async () => {
    while (index < items.length) {
      const currentIndex = index++;
      await worker(items[currentIndex]);
    }
  });

  await Promise.all(runners);
}

const useMealTimestampRangeKpis = ({
  kitchen,
  startDate,
  endDate,
  enabled = true,
  includeTimestamps = false
}: UseMealTimestampRangeKpisParams) => {
  const client = useApolloClient();
  const [totalMealsProcessed, setTotalMealsProcessed] = useState(0);
  const [timestamps, setTimestamps] = useState<MealTimestampListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const dates = useMemo(() => toDateList(startDate, endDate), [startDate, endDate]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!enabled || !kitchen || dates.length === 0) {
        if (!cancelled) {
          setTotalMealsProcessed(0);
          setTimestamps([]);
          setError(null);
          setLoading(false);
        }
        return;
      }

      setLoading(true);
      setError(null);

      try {
        let mealCount = 0;
        const allTimestamps: MealTimestampListItem[] = [];
        await runWithConcurrency(dates, MAX_CONCURRENT_PARTITION_QUERIES, async (date: string) => {
          let next: string | undefined;
          do {
            const response = await client.query<ListMealTimestampsResponse>({
              query: LIST_MEAL_TIMESTAMPS_QUERY,
              variables: {
                kitchen,
                date,
                next
              },
              fetchPolicy: 'network-only'
            });

            const page = response.data?.listMealTimestamps;
            const pageData = page?.data ?? [];
            mealCount += pageData.length;
            if (includeTimestamps && pageData.length > 0) {
              allTimestamps.push(
                ...pageData.map((item) => ({
                  ...item,
                  station: item.station ?? undefined,
                  date
                }))
              );
            }
            next = page?.next ?? undefined;
          } while (next);
        });

        if (!cancelled) {
          setTotalMealsProcessed(mealCount);
          setTimestamps(includeTimestamps ? allTimestamps : []);
        }
      } catch (error_) {
        if (!cancelled) {
          setTotalMealsProcessed(0);
          setTimestamps([]);
          setError(error_ instanceof Error ? error_ : new Error('Failed to load meal timestamp metrics.'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [client, dates, enabled, includeTimestamps, kitchen]);

  return {
    totalMealsProcessed,
    timestamps,
    mealTimestampMetricsLoading: loading,
    mealTimestampMetricsError: error
  };
};

export default useMealTimestampRangeKpis;
